import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  OnChanges,
  OnInit,
  type SimpleChanges,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-multi-select-dropdown',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIcon],
  templateUrl: './common-multi-select-component-dropdown-search.component.html',
  styleUrls: ['./common-multi-select-component-dropdown-search.component.scss'],
})
export class MultiSelectDropdownComponent implements OnInit, OnChanges {
  @Input() items: { key: string; value: string }[] = [];
  @Input() placeHolder = 'Select';
  @Input() initialValue: string | null = null;

  @Output() selectionChange = new EventEmitter<string[]>();

  dropdownVisible = false;

  searchControl = new FormControl('');
  filteredItems: { key: string; value: string }[] = [];

  selectedItems: { key: string; value: string }[] = [];

  constructor(private el: ElementRef) {
    this.searchControl.valueChanges.subscribe(() => {
      this.filterItems();
    });
  }

  ngOnInit() {
    this.filteredItems = [...this.items];
    this.setInitialSelection();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['items']) {
      this.filteredItems = [...this.items];
      this.setInitialSelection(); // 🔥 important
    }

    if (changes['initialValue']) {
      this.setInitialSelection(); // 🔥 handles dynamic updates
    }
  }

  toggleDropdown(event: MouseEvent) {
    event.stopPropagation();
    this.dropdownVisible = !this.dropdownVisible;
    if (this.dropdownVisible) {
      this.filteredItems = [...this.items];
    }
  }

  @HostListener('document:click', ['$event'])
  close(event: MouseEvent) {
    if (!this.el.nativeElement.contains(event.target)) {
      if (this.dropdownVisible) {
        this.emitSelection();
      }
      this.dropdownVisible = false;
    }
  }

  filterItems() {
    const term = this.searchControl.value?.toLowerCase() || '';

    this.filteredItems = this.items.filter((item) =>
      item.key.toLowerCase().includes(term)
    );
  }

  private setInitialSelection() {
    if (!this.initialValue || !this.items?.length) return;

    const values = this.initialValue.split(',').map((v) => v.trim());

    this.selectedItems = this.items.filter((item) =>
      values.includes(item.value)
    );
  }

  isSelected(item: { key: string; value: string }): boolean {
    return this.selectedItems.some((i) => i.value === item.value);
  }

  onItemToggle(item: { key: string; value: string }) {
    if (this.isSelected(item)) {
      this.selectedItems = this.selectedItems.filter(
        (i) => i.value !== item.value
      );
    } else {
      this.selectedItems.push(item);
    }
  }

  applySelection(event: MouseEvent) {
    event.stopPropagation();

    this.emitSelection(); // 🔥 emit only here
    this.dropdownVisible = false;
  }

  removeItem(item: { key: string; value: string }, event: MouseEvent) {
    event.stopPropagation();

    this.selectedItems = this.selectedItems.filter(
      (i) => i.value !== item.value
    );

    this.emitSelection();
  }

  clearAll(event: MouseEvent) {
    event.stopPropagation();
    this.selectedItems = [];
    this.emitSelection();
  }

  private emitSelection() {
    this.selectionChange.emit(this.selectedItems.map((i) => i.value));
  }
}
